import { useState, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import {
  Home,
  Users,
  Plus,
  BarChart2,
  Settings,
  CreditCard,
  LogOut,
  Menu,
} from "lucide-react";
import { NotificationBell } from "@/components/NotificationBell";
import { BrandLockup } from "@/shell/CandidateFlow";
import { api } from "@/lib/api";
import { applyBranding } from "@/lib/branding";
import type { BrandingData } from "@/lib/branding";

export interface RecruiterShellProps {
  children: ReactNode;
  activeNav: "home" | "candidates" | "invite" | "reports" | "settings" | "billing";
}

const NAV_ITEMS = [
  { key: "home", label: "Home", href: "/coach/recruiter", Icon: Home },
  { key: "candidates", label: "Candidates", href: "/coach/recruiter/candidates", Icon: Users },
  { key: "invite", label: "Send invite", href: "/coach/recruiter/invite/new", Icon: Plus },
  { key: "reports", label: "Reports", href: "/coach/recruiter/reports", Icon: BarChart2 },
  { key: "settings", label: "Settings", href: "/coach/recruiter/settings", Icon: Settings },
  { key: "billing", label: "Billing", href: "/coach/recruiter/billing", Icon: CreditCard },
] as const;

// Single top header at every breakpoint (mirrors candidate HomeShell) — the
// sidebar + bottom tab bar this used to be are gone; navigation lives behind
// the hamburger menu instead. Andrew's call: the candidate lockup treatment
// reads cleaner than trying to fit it in a 160px sidebar column.
const BRANDING_CACHE_KEY = "recruiter-branding-cache";

function readCachedBranding(): BrandingData | null {
  try {
    const cached = sessionStorage.getItem(BRANDING_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

export function RecruiterShell({ children, activeNav }: RecruiterShellProps) {
  const [loggingOut, setLoggingOut] = useState(false);
  // Every recruiter route is its own top-level page (no shared layout route),
  // so this shell fully unmounts/remounts on each navigation. Starting from
  // `null` meant the header briefly rendered the no-agency lockup state on
  // every single page change while /branding re-fetched — a visible flash.
  // Seeding from a same-tab cache means every navigation after the first
  // paints the last-known branding immediately; the fetch below still runs
  // to catch real changes (e.g. after a Brand Setup save).
  const [branding, setBranding] = useState<BrandingData | null>(readCachedBranding);
  const menuRef = useRef<HTMLDetailsElement>(null);

  // Paint the cached brand before the first frame, not after the fetch returns —
  // otherwise every recruiter page flashes house plum on the way in.
  const cached = readCachedBranding();
  if (cached) applyBranding(cached);

  useEffect(() => {
    const load = () => api.get<BrandingData>("/branding").then((b) => {
      setBranding(b);
      // The shell previously read only the logo off this response and dropped
      // `cssVars` on the floor, so a recruiter who set a brand colour saw it
      // nowhere in their own portal. applyBranding injects the server-computed
      // :root block that every var(--accent) below resolves against.
      applyBranding(b);
      try { sessionStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(b)); } catch { /* ignore */ }
    }).catch(() => {});
    load();
    // Same-page refresh after Brand Setup saves, without a full navigation/remount.
    window.addEventListener("branding:updated", load);
    return () => window.removeEventListener("branding:updated", load);
  }, []);

  // Native <details> doesn't close on an outside click — fine for a secondary
  // profile menu, but this is the primary nav now, so it needs to behave like one.
  useEffect(() => {
    function closeIfOutside(e: MouseEvent) {
      if (menuRef.current?.open && !menuRef.current.contains(e.target as Node)) {
        menuRef.current.open = false;
      }
    }
    document.addEventListener("click", closeIfOutside);
    return () => document.removeEventListener("click", closeIfOutside);
  }, []);

  const onLogoutClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (loggingOut) return;
    setLoggingOut(true);
    window.location.href = "/auth/logout";
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--paper-2)" }}>
      <header
        style={{
          minHeight: 60,
          background: "var(--cream)",
          borderBottom: "1px solid var(--hair)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "10px 16px",
          flexShrink: 0,
        }}
      >
        <a href="/coach/recruiter" style={{ textDecoration: "none" }} aria-label="Go to home">
          {/* Desktop: horizontal "logo | Powered by [mark] Executive Interview Coach".
              Mobile: the horizontal row doesn't fit next to the logo + bell + menu in
              ~280px, and BrandLockup's inline mode hides the "Powered by" wordmark
              below md: — so mobile gets the stacked variant instead of losing it. */}
          <div className="hidden md:flex">
            <BrandLockup
              agencyName={branding?.agencyName}
              logoUrl={branding?.logoUrlLight}
              logoFilter={branding?.logoFilter}
              inline
            />
          </div>
          <div className="flex md:hidden">
            <BrandLockup
              agencyName={branding?.agencyName}
              logoUrl={branding?.logoUrlLight}
              logoFilter={branding?.logoFilter}
              align="left"
            />
          </div>
        </a>

        <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
          <NotificationBell />
          <details ref={menuRef} style={{ position: "relative" }}>
            <summary
              aria-label="Menu"
              style={{
                listStyle: "none",
                cursor: "pointer",
                width: 36,
                height: 36,
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--slate-700)",
              }}
            >
              <Menu size={20} strokeWidth={1.75} />
            </summary>
            <nav
              aria-label="Recruiter navigation"
              style={{
                position: "absolute",
                right: 0,
                marginTop: 8,
                minWidth: 200,
                background: "var(--cream)",
                border: "1px solid var(--hair)",
                borderRadius: 12,
                boxShadow: "0 8px 24px rgba(27,26,36,0.12)",
                padding: "6px 0",
                zIndex: 50,
              }}
            >
              {NAV_ITEMS.map(({ key, label, href, Icon }) => {
                const isActive = activeNav === key;
                return (
                  <a
                    key={key}
                    href={href}
                    aria-current={isActive ? "page" : undefined}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "9px 16px",
                      textDecoration: "none",
                      fontSize: 13.5,
                      fontWeight: 500,
                      color: isActive ? "var(--accent)" : "var(--slate-700)",
                      background: isActive ? "var(--accent-50)" : "transparent",
                    }}
                  >
                    <Icon size={16} strokeWidth={isActive ? 2 : 1.75} />
                    {label}
                  </a>
                );
              })}
              <div style={{ borderTop: "1px solid var(--hair)", margin: "6px 0" }} />
              <button
                onClick={onLogoutClick}
                disabled={loggingOut}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "9px 16px",
                  background: "transparent",
                  border: "none",
                  cursor: loggingOut ? "default" : "pointer",
                  fontSize: 13.5,
                  fontWeight: 500,
                  color: "var(--slate-500)",
                  textAlign: "left",
                  opacity: loggingOut ? 0.6 : 1,
                }}
              >
                <LogOut size={16} strokeWidth={1.75} />
                {loggingOut ? "Logging out…" : "Log out"}
              </button>
            </nav>
          </details>
        </div>
      </header>

      {/* Same rule as HomeShell's main. The recruiter shell previously set no
          max width and no centring, so every page picked its own (600, 720,
          760, 560) and pinned itself to the left edge of a wide monitor while
          the rest of the app centred. One column, one place. */}
      <main className="recruiter-main flex-1 w-full max-w-[1160px] mx-auto px-4 md:px-8 py-6 md:py-8">
        {children}
      </main>
    </div>
  );
}
